import React, { useState, useEffect } from 'react';
import api from '../services/api';
import './AdminDashboard.css';
import AdminAnalytics from './AdminAnalytics';

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('dashboard'); // 'dashboard' | 'clients' | 'create_client' | 'tickets' | 'analytics' | 'parametres'
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  
  // Form State
  const [formData, setFormData] = useState({
    nom: '',
    siren: '',
    email: '',
    password: '',
    confirmPassword: '',
    regime: 'reel_normal',
    frequence: 'mensuelle'
  });
  
  // Data States
  const [clients, setClients] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({
    total_clients: 0,
    active_clients: 0,
    regimes: {},
    tickets_statut: { ouvert: 0, en_cours: 0, resolu: 0, ferme: 0 },
    tickets_priorite: { basse: 0, moyenne: 0, haute: 0 }
  });

  // UI States
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Fetch all data on mount or when tab changes
  useEffect(() => {
    fetchStats();
    fetchClients();
    fetchTickets();
  }, [activeTab]);

  // Autoclose messages after 5 seconds
  useEffect(() => {
    if (message) {
      const timer = setTimeout(() => {
        setMessage(null);
      }, 7000);
      return () => clearTimeout(timer);
    }
  }, [message]);

  const fetchStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      if (res.success && res.stats) {
        setStats(res.stats);
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des stats:', err);
    }
  };

  const fetchClients = async () => {
    try {
      const res = await api.get('/admin/clients');
      if (res.success && res.clients) {
        setClients(res.clients);
      }
    } catch (err) {
      console.error('Erreur lors du chargement des clients:', err);
    }
  };

  const fetchTickets = async () => {
    try {
      const res = await api.get('/admin/tickets');
      if (res.success && res.tickets) {
        setTickets(res.tickets);
        // Mettre à jour le ticket sélectionné s'il y en a un
        if (selectedTicket) {
          const updated = res.tickets.find(t => t.id === selectedTicket.id);
          if (updated) setSelectedTicket(updated);
        }
      }
    } catch (err) {
      console.error('Erreur lors du chargement des tickets:', err);
    }
  };

  const handleDeleteClient = async (clientId, clientNom) => {
    if (!window.confirm(`⚠️ DANGER : Êtes-vous sûr de vouloir supprimer définitivement le client "${clientNom}" ?\n\nCette action détruira définitivement son schéma isolé PostgreSQL (tables de documents, écritures, etc.) et est totalement irréversible.`)) {
      return;
    }
    
    setLoading(true);
    try {
      const res = await api.delete(`/admin/clients/${clientId}`);
      if (res.success) {
        setMessage({
          type: 'success',
          text: `🗑️ Le client "${clientNom}" et toutes ses données associées ont été supprimés.`
        });
        setSelectedClient(null); // fermer la fiche si ouverte
        fetchClients();
        fetchStats();
      } else {
        setMessage({ type: 'error', text: res.message || res.error || 'Erreur lors de la suppression' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur lors de la suppression' });
    } finally {
      setLoading(false);
    }
  };

  const validateSIREN = (siren) => {
    return /^[0-9]{9}$/.test(siren);
  };

  const validateEmail = (email) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  const validatePassword = (password) => {
    if (password.length < 8) {
      return { valid: false, message: 'Le mot de passe doit contenir au moins 8 caractères' };
    }
    if (!/[A-Z]/.test(password)) {
      return { valid: false, message: 'Le mot de passe doit contenir au moins une majuscule' };
    }
    if (!/[a-z]/.test(password)) {
      return { valid: false, message: 'Le mot de passe doit contenir au moins une minuscule' };
    }
    if (!/[0-9]/.test(password)) {
      return { valid: false, message: 'Le mot de passe doit contenir au moins un chiffre' };
    }
    return { valid: true, message: '' };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);

    // Validations
    if (!formData.nom.trim()) {
      setMessage({ type: 'error', text: 'Le nom du client est requis' });
      return;
    }
    if (!validateSIREN(formData.siren)) {
      setMessage({ type: 'error', text: 'Le SIREN doit contenir exactement 9 chiffres' });
      return;
    }
    if (!validateEmail(formData.email)) {
      setMessage({ type: 'error', text: 'Format email invalide' });
      return;
    }
    const passwordValidation = validatePassword(formData.password);
    if (!passwordValidation.valid) {
      setMessage({ type: 'error', text: passwordValidation.message });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setMessage({ type: 'error', text: 'Les mots de passe ne correspondent pas' });
      return;
    }

    setLoading(true);

    try {
      const clientId = `client_${formData.siren}`;
      const response = await api.post('/admin/clients', {
        id: clientId,
        nom: formData.nom,
        siren: formData.siren,
        email: formData.email,
        password: formData.password,
        regime_tva: formData.regime,
        frequence_tva: formData.frequence
      });

      // CORRECTION DU BUG : response.client au lieu de response.data.client
      const clientInfo = response.client || response.data?.client;

      if (response.success && clientInfo) {
        setMessage({
          type: 'success',
          text: `✅ Client "${formData.nom}" créé avec succès !`,
          details: `📧 Connexion : ${clientInfo.email} | 🔑 Mot de passe : celui choisi à la création`
        });
        
        // Réinitialiser le formulaire
        setFormData({
          nom: '',
          siren: '',
          email: '',
          password: '',
          confirmPassword: '',
          regime: 'reel_normal',
          frequence: 'mensuelle'
        });

        // Recharger les données
        fetchClients();
        fetchStats();
      } else {
        setMessage({ type: 'error', text: response.message || response.error || 'Erreur lors de la création' });
      }
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur de connexion au serveur ou privilèges insuffisants' });
    } finally {
      setLoading(false);
    }
  };

  const handleSendReply = async (e) => {
    e.preventDefault();
    if (!replyText.trim() || !selectedTicket) return;

    setActionLoading(true);
    try {
      const res = await api.post(`/admin/tickets/${selectedTicket.id}/messages`, {
        text: replyText
      });
      if (res.success && res.messages) {
        setReplyText('');
        fetchTickets();
      }
    } catch (err) {
      console.error('Erreur lors de la réponse au ticket:', err);
    } finally {
      setActionLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    if (!selectedTicket) return;
    try {
      const res = await api.put(`/admin/tickets/${selectedTicket.id}/status`, {
        statut: newStatus
      });
      if (res.success) {
        fetchTickets();
      }
    } catch (err) {
      console.error('Erreur lors du changement de statut:', err);
    }
  };

  // Helper labels
  const getRegimeLabel = (regime) => {
    switch (regime) {
      case 'reel_normal': return 'Réel Normal';
      case 'reel_simplifie': return 'Réel Simplifié';
      case 'franchise': return 'Franchise de Base';
      default: return regime;
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'haute': return { bg: '#FCEBEB', text: '#A32D2D', border: '#F5B5B5', icon: '🔴' };
      case 'moyenne': return { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A', icon: '🟡' };
      case 'basse': return { bg: '#E1F5EE', text: '#0F6E56', border: '#A7F3D0', icon: '🟢' };
      default: return { bg: '#F3F4F6', text: '#374151', border: '#E5E7EB', icon: '⚪' };
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'ouvert': return { label: 'Ouvert', bg: '#FCEBEB', text: '#A32D2D', border: '#F5B5B5' };
      case 'en_cours': return { label: 'En cours', bg: '#E8F4FD', text: '#1D4ED8', border: '#B8DFF8' };
      case 'resolu': return { label: 'Résolu', bg: '#E1F5EE', text: '#0F6E56', border: '#5DCAA5' };
      case 'ferme': return { label: 'Fermé', bg: '#F3F4F6', text: '#4B5563', border: '#D1D5DB' };
      default: return { label: status, bg: '#F3F4F6', text: '#374151', border: '#E5E7EB' };
    }
  };

  return (
    <div className="admin-dashboard-root" style={{ display: 'flex', height: '100vh', width: '100vw', background: '#F5F4ED', fontFamily: 'Inter, system-ui, sans-serif', overflow: 'hidden' }}>
      
      {/* Mobile Drawer Overlay */}
      <div 
        className={`admin-sidebar-overlay ${isMenuOpen ? 'open' : ''}`} 
        onClick={() => setIsMenuOpen(false)}
      />
      
      {/* Sidebar Latérale de Navigation */}
      <div className={`admin-sidebar ${isMenuOpen ? 'open' : ''}`} style={{ width: '260px', background: '#1F2937', color: 'white', display: 'flex', flexDirection: 'column', flexShrink: '0', boxShadow: '2px 0 8px rgba(0,0,0,0.1)' }}>
        
        {/* Sidebar Header */}
        <div style={{ padding: '24px 20px', borderBottom: '1px solid #374151', display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{ width: '38px', height: '38px', borderRadius: '10px', background: '#1D9E75', color: 'white', fontSize: '20px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            C
          </div>
          <div>
            <div style={{ fontSize: '15px', fontWeight: '600', letterSpacing: '0.5px' }}>ComptaAI Admin</div>
            <div style={{ fontSize: '11px', color: '#9CA3AF' }}>Espace de Contrôle</div>
          </div>
        </div>

        {/* Navigation Items */}
        <div className="nav-items" style={{ flex: 1, padding: '20px 12px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
          <button
            onClick={() => { setActiveTab('dashboard'); setIsMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === 'dashboard' ? '#1D9E75' : 'transparent',
              color: activeTab === 'dashboard' ? 'white' : '#D1D5DB',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '16px' }}>📊</span> Tableau de bord
          </button>
          
          <button
            onClick={() => { setActiveTab('clients'); setIsMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === 'clients' ? '#1D9E75' : 'transparent',
              color: activeTab === 'clients' ? 'white' : '#D1D5DB',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '16px' }}>👥</span> Gestion des clients
          </button>

          <button
            onClick={() => { setActiveTab('create_client'); setIsMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === 'create_client' ? '#1D9E75' : 'transparent',
              color: activeTab === 'create_client' ? 'white' : '#D1D5DB',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '16px' }}>➕</span> Créer un client
          </button>

          <button
            onClick={() => { setActiveTab('tickets'); setIsMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === 'tickets' ? '#1D9E75' : 'transparent',
              color: activeTab === 'tickets' ? 'white' : '#D1D5DB',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '16px' }}>🎫</span> Tickets de support
            {tickets.filter(t => t.statut === 'ouvert').length > 0 && (
              <span style={{ marginLeft: 'auto', background: '#EF4444', color: 'white', fontSize: '10px', padding: '2px 6px', borderRadius: '10px', fontWeight: 'bold' }}>
                {tickets.filter(t => t.statut === 'ouvert').length}
              </span>
            )}
          </button>

          <button
            onClick={() => { setActiveTab('analytics'); setIsMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === 'analytics' ? '#1D9E75' : 'transparent',
              color: activeTab === 'analytics' ? 'white' : '#D1D5DB',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '16px' }}>📈</span> Analytics
          </button>

          <button
            onClick={() => { setActiveTab('parametres'); setIsMenuOpen(false); }}
            style={{
              display: 'flex', alignItems: 'center', gap: '12px', width: '100%', padding: '12px 16px', borderRadius: '8px', border: 'none', fontSize: '14px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s',
              background: activeTab === 'parametres' ? '#1D9E75' : 'transparent',
              color: activeTab === 'parametres' ? 'white' : '#D1D5DB',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: '16px' }}>⚙️</span> Paramètres
          </button>
        </div>

        {/* Sidebar Footer */}
        <div style={{ padding: '20px', borderTop: '1px solid #374151', display: 'flex', flexDirection: 'column', gap: '10px' }}>
          <div style={{ fontSize: '12px', color: '#9CA3AF', textAlign: 'center' }}>Connecté en Admin</div>
          <button
            onClick={onLogout}
            style={{
              width: '100%', padding: '10px', border: '1px solid #4B5563', borderRadius: '8px', background: 'transparent', color: '#F3F4F6', fontSize: '13px', fontWeight: '500', cursor: 'pointer', transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => { e.target.style.background = '#374151'; }}
            onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
          >
            Se déconnecter
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="admin-main-content" style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        
        {/* Header de Page */}
        <div className="admin-header" style={{ background: 'white', borderBottom: '1px solid #E8E7E3', padding: '20px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <button 
              className="admin-hamburger-btn" 
              onClick={() => setIsMenuOpen(true)}
              title="Ouvrir le menu"
            >
              ☰
            </button>
            <div>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#1F2937', margin: 0 }}>
                {activeTab === 'dashboard' && '📊 Tableau de Bord de Suivi'}
                {activeTab === 'clients' && '👥 Gestion Générale des Clients'}
                {activeTab === 'create_client' && '➕ Enregistrement d\'un Nouveau Client'}
                {activeTab === 'tickets' && '🎫 Espace Tickets d\'Assistance'}
                {activeTab === 'analytics' && '📈 Analytics & Suivi d\'Audience'}
                {activeTab === 'parametres' && '⚙️ Paramètres de l\'Administrateur'}
              </h2>
              <p style={{ fontSize: '13px', color: '#73726C', margin: '4px 0 0' }}>
                {activeTab === 'dashboard' && 'Statistiques globales, répartition et activité du cabinet.'}
                {activeTab === 'clients' && `Consultez et gérez les comptes des ${clients.length} clients du cabinet.`}
                {activeTab === 'create_client' && 'Créez un nouveau client avec son schéma PostgreSQL dédié.'}
                {activeTab === 'tickets' && `Messagerie et support pour les demandes d'assistance.`}
                {activeTab === 'analytics' && 'Audience, activité produit, revenus et erreurs (statistiques anonymes).'}
                {activeTab === 'parametres' && 'Gérez les configurations système et changez de mot de passe.'}
              </p>
            </div>
          </div>
          
          <div style={{ fontSize: '13px', color: '#73726C', background: '#FAF9F5', padding: '8px 16px', borderRadius: '20px', border: '1px solid #E8E7E3' }}>
            📅 Aujourd'hui : <strong>{new Date().toLocaleDateString('fr-FR', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</strong>
          </div>
        </div>

        {/* Scrollable Content Container */}
        <div style={{ flex: 1, padding: '32px', overflowY: 'auto' }}>
          
          {/* Notifications / Messages flash */}
          {message && (
            <div style={{
              background: message.type === 'success' ? '#E1F5EE' : '#FCEBEB',
              border: `1px solid ${message.type === 'success' ? '#5DCAA5' : '#F5B5B5'}`,
              borderRadius: '12px',
              padding: '16px 20px',
              marginBottom: '28px',
              fontSize: '14px',
              color: message.type === 'success' ? '#0F6E56' : '#A32D2D',
              boxShadow: '0 2px 4px rgba(0,0,0,0.02)'
            }}>
              <div><strong>{message.text}</strong></div>
              {message.details && (
                <div style={{ marginTop: '8px', fontSize: '13px', padding: '10px', background: 'rgba(255,255,255,0.5)', borderRadius: '6px', border: '1px dashed rgba(0,0,0,0.05)', color: '#2D2D2D' }}>
                  {message.details}
                </div>
              )}
            </div>
          )}

          {/* TAB 1: DASHBOARD STATS */}
          {activeTab === 'dashboard' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              
              {/* KPI Cards Grid */}
              <div className="admin-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '20px' }}>
                <div style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: '32px', padding: '12px', background: '#E1F5EE', borderRadius: '12px' }}>👥</div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#73726C', fontWeight: '500' }}>Total Clients</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#1F2937', marginTop: '4px' }}>{stats.total_clients}</div>
                  </div>
                </div>

                <div style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: '32px', padding: '12px', background: '#E8F4FD', borderRadius: '12px' }}>⚡</div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#73726C', fontWeight: '500' }}>Clients Actifs</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#1D9E75', marginTop: '4px' }}>{stats.active_clients}</div>
                  </div>
                </div>

                <div style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: '32px', padding: '12px', background: '#FCEBEB', borderRadius: '12px' }}>🚨</div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#73726C', fontWeight: '500' }}>Tickets Ouverts</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#EF4444', marginTop: '4px' }}>{stats.tickets_statut.ouvert}</div>
                  </div>
                </div>

                <div style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '24px', display: 'flex', alignItems: 'center', gap: '20px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <div style={{ fontSize: '32px', padding: '12px', background: '#FEF3C7', borderRadius: '12px' }}>🎫</div>
                  <div>
                    <div style={{ fontSize: '13px', color: '#73726C', fontWeight: '500' }}>Support En cours</div>
                    <div style={{ fontSize: '28px', fontWeight: '700', color: '#F59E0B', marginTop: '4px' }}>{stats.tickets_statut.en_cours}</div>
                  </div>
                </div>
              </div>

              {/* Analytique Sections Grid */}
              <div className="admin-charts-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '28px' }}>
                
                {/* Régimes TVA Chart list */}
                <div style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    📊 Répartition des Clients par Régime TVA
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
                    {['reel_normal', 'reel_simplifie', 'franchise'].map(regime => {
                      const count = stats.regimes[regime] || 0;
                      const percentage = stats.total_clients > 0 ? Math.round((count / stats.total_clients) * 100) : 0;
                      return (
                        <div key={regime}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: '#4B5563', marginBottom: '6px' }}>
                            <span>{getRegimeLabel(regime)}</span>
                            <strong>{count} client(s) ({percentage}%)</strong>
                          </div>
                          <div style={{ height: '8px', background: '#E5E7EB', borderRadius: '4px', overflow: 'hidden' }}>
                            <div style={{ width: `${percentage}%`, height: '100%', background: '#1D9E75', borderRadius: '4px' }} />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Support Tickets overview */}
                <div style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '28px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1F2937', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                    🎫 Métriques de Support d'Assistance
                  </h3>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyItems: 'space-between', padding: '12px 16px', background: '#FAF9F5', borderRadius: '8px', border: '1px solid #E8E7E3' }}>
                      <span style={{ fontSize: '14px', color: '#4B5563' }}>Tickets résolus / clos</span>
                      <strong style={{ marginLeft: 'auto', fontSize: '16px', color: '#10B981' }}>
                        {stats.tickets_statut.resolu + stats.tickets_statut.ferme}
                      </strong>
                    </div>
                    
                    <div style={{ display: 'flex', alignItems: 'center', justifyItems: 'space-between', padding: '12px 16px', background: '#FAF9F5', borderRadius: '8px', border: '1px solid #E8E7E3' }}>
                      <span style={{ fontSize: '14px', color: '#4B5563' }}>Demandes en attente (Ouverts)</span>
                      <strong style={{ marginLeft: 'auto', fontSize: '16px', color: '#EF4444' }}>
                        {stats.tickets_statut.ouvert}
                      </strong>
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', justifyItems: 'space-between', padding: '12px 16px', background: '#FAF9F5', borderRadius: '8px', border: '1px solid #E8E7E3' }}>
                      <span style={{ fontSize: '14px', color: '#4B5563' }}>Priorité Haute</span>
                      <strong style={{ marginLeft: 'auto', fontSize: '16px', color: '#B91C1C' }}>
                        {stats.tickets_priorite.haute}
                      </strong>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* TAB ANALYTICS */}
          {activeTab === 'analytics' && (
            <AdminAnalytics />
          )}

          {/* TAB 2: CLIENTS LIST */}
          {activeTab === 'clients' && (
            <div className="admin-table-wrapper" style={{ background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              {clients.length === 0 ? (
                <div style={{ padding: '60px', textAlign: 'center', color: '#73726C' }}>
                  <div style={{ fontSize: '40px', marginBottom: '16px' }}>👥</div>
                  <h3>Aucun client n'a été créé pour le moment.</h3>
                  <p style={{ marginTop: '8px', fontSize: '14px' }}>Allez sur l'onglet <strong>Créer un client</strong> pour enregistrer votre premier dossier.</p>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                  <thead>
                    <tr style={{ background: '#FAF9F5', borderBottom: '1px solid #E8E7E3' }}>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Client</th>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>SIREN</th>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Identifiant DB</th>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Régime TVA</th>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563' }}>Date de création</th>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563', textAlign: 'center' }}>État</th>
                      <th style={{ padding: '18px 24px', fontSize: '13px', fontWeight: '600', color: '#4B5563', textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr 
                        key={c.id} 
                        onClick={() => setSelectedClient(c)}
                        style={{ borderBottom: '1px solid #F3F4F6', transition: 'background 0.15s', cursor: 'pointer' }}
                        onMouseEnter={(e) => { e.currentTarget.style.background = '#FAF9F5'; }}
                        onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                      >
                        <td style={{ padding: '18px 24px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                            <div style={{ width: '38px', height: '38px', borderRadius: '50%', background: '#E1F5EE', color: '#0F6E56', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold' }}>
                              {c.nom.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div style={{ fontSize: '14px', fontWeight: '600', color: '#1F2937' }}>{c.nom}</div>
                              <div style={{ fontSize: '12px', color: '#73726C', marginTop: '2px' }}>{c.email || 'Pas d\'email'}</div>
                            </div>
                          </div>
                        </td>
                        <td style={{ padding: '18px 24px', fontSize: '14px', color: '#4B5563' }}>{c.siren || 'N/A'}</td>
                        <td style={{ padding: '18px 24px', fontSize: '13px', color: '#6B7280', fontFamily: 'monospace' }}>{c.id}</td>
                        <td style={{ padding: '18px 24px' }}>
                          <span style={{ fontSize: '12px', padding: '4px 10px', background: '#E8F4FD', color: '#1D4ED8', borderRadius: '12px', border: '1px solid #B8DFF8', fontWeight: '500' }}>
                            {getRegimeLabel(c.regime_tva)}
                          </span>
                        </td>
                        <td style={{ padding: '18px 24px', fontSize: '13px', color: '#4B5563' }}>
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                        </td>
                        <td style={{ padding: '18px 24px', textAlign: 'center' }}>
                          <span style={{
                            fontSize: '11px', padding: '4px 10px', borderRadius: '12px', fontWeight: '600',
                            background: c.actif ? '#E1F5EE' : '#FCEBEB',
                            color: c.actif ? '#0F6E56' : '#A32D2D',
                            border: c.actif ? '1px solid #5DCAA5' : '1px solid #F5B5B5'
                          }}>
                            {c.actif ? '✓ ACTIF' : '✕ INACTIF'}
                          </span>
                        </td>
                        <td style={{ padding: '18px 24px', textAlign: 'center' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClient(c.id, c.nom);
                            }}
                            title="Supprimer ce client"
                            style={{ background: 'transparent', border: 'none', fontSize: '16px', cursor: 'pointer', padding: '6px', borderRadius: '4px', color: '#EF4444', transition: 'background 0.2s' }}
                            onMouseEnter={(e) => { e.target.style.background = '#FCEBEB'; }}
                            onMouseLeave={(e) => { e.target.style.background = 'transparent'; }}
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* TAB 3: CREATE CLIENT FORM */}
          {activeTab === 'create_client' && (
            <div style={{ maxWidth: '680px', margin: '0 auto', background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', padding: '32px', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                <div className="admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Nom client */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                      Nom client <span style={{ color: '#D85A30' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      placeholder="Ex: Anges Mobilité"
                      required
                      style={{ width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                      onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
                    />
                  </div>

                  {/* SIREN */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                      SIREN <span style={{ color: '#D85A30' }}>*</span>
                    </label>
                    <input
                      type="text"
                      value={formData.siren}
                      onChange={(e) => {
                        const onlyNumbers = e.target.value.replace(/\D/g, '');
                        setFormData({ ...formData, siren: onlyNumbers.slice(0, 9) });
                      }}
                      placeholder="9 chiffres"
                      required
                      maxLength={9}
                      style={{ width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                      onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
                    />
                    <div style={{ fontSize: '11px', color: formData.siren.length === 9 ? '#1D9E75' : '#73726C', marginTop: '4px', fontWeight: formData.siren.length === 9 ? 'bold' : 'normal' }}>
                      {formData.siren.length}/9 chiffres
                    </div>
                  </div>
                </div>

                <div className="admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Email */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                      Email de connexion <span style={{ color: '#D85A30' }}>*</span>
                    </label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="contact@angesmobilite.fr"
                      required
                      style={{ width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                      onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
                    />
                  </div>

                  {/* Mot de passe */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                      Mot de passe <span style={{ color: '#D85A30' }}>*</span>
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        placeholder="Min 8 car., 1 Maj, 1 Min, 1 Chif."
                        required
                        style={{ width: '100%', padding: '12px', paddingRight: '40px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }}
                        onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                        onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                      >
                        {showPassword ? '🙈' : '👁️'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Confirmation mot de passe */}
                <div>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                    Confirmer le mot de passe <span style={{ color: '#D85A30' }}>*</span>
                  </label>
                  <div style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={formData.confirmPassword}
                      onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                      placeholder="Saisissez à nouveau le mot de passe"
                      required
                      style={{ width: '100%', padding: '12px', paddingRight: '40px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', transition: 'border-color 0.2s' }}
                      onFocus={(e) => e.target.style.borderColor = '#1D9E75'}
                      onBlur={(e) => e.target.style.borderColor = '#E8E7E3'}
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', padding: '0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      {showConfirmPassword ? '🙈' : '👁️'}
                    </button>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  {/* Régime TVA */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                      Régime TVA
                    </label>
                    <select
                      value={formData.regime}
                      onChange={(e) => setFormData({ ...formData, regime: e.target.value })}
                      style={{ width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', background: 'white' }}
                    >
                      <option value="reel_normal">Réel normal (CA3)</option>
                      <option value="reel_simplifie">Réel simplifié</option>
                      <option value="franchise">Franchise de base</option>
                    </select>
                  </div>

                  {/* Fréquence TVA */}
                  <div>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '500', color: '#2D2D2D', marginBottom: '8px' }}>
                      Fréquence TVA
                    </label>
                    <select
                      value={formData.frequence}
                      onChange={(e) => setFormData({ ...formData, frequence: e.target.value })}
                      style={{ width: '100%', padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '14px', outline: 'none', background: 'white' }}
                    >
                      <option value="mensuelle">Mensuelle</option>
                      <option value="trimestrielle">Trimestrielle</option>
                    </select>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%', padding: '14px', background: loading ? '#73726C' : '#1D9E75', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: loading ? 'not-allowed' : 'pointer', transition: 'background 0.2s', marginTop: '10px'
                  }}
                  onMouseEnter={(e) => !loading && (e.target.style.background = '#0F6E56')}
                  onMouseLeave={(e) => e.target.style.background = '#1D9E75'}
                >
                  {loading ? 'Création du schéma PostgreSQL en cours...' : 'Créer et configurer le dossier client'}
                </button>
              </form>
            </div>
          )}

          {/* TAB 4: SUPPORT TICKETS */}
          {activeTab === 'tickets' && (
            <div style={{ display: 'flex', background: 'white', border: '1px solid #E8E7E3', borderRadius: '16px', height: '620px', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
              
              {/* Inbox Tickets (Left) */}
              <div style={{ width: '320px', borderRight: '1px solid #E8E7E3', display: 'flex', flexDirection: 'column', flexShrink: '0' }}>
                <div style={{ padding: '16px', background: '#FAF9F5', borderBottom: '1px solid #E8E7E3', fontWeight: '600', fontSize: '14px', color: '#1F2937' }}>
                  📬 Boîte de Réception ({tickets.length})
                </div>
                <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column' }}>
                  {tickets.length === 0 ? (
                    <div style={{ padding: '40px 20px', textAlign: 'center', color: '#73726C', fontSize: '13px' }}>
                      Aucun ticket d'assistance trouvé.
                    </div>
                  ) : (
                    tickets.map((t) => {
                      const pInfo = getPriorityColor(t.priorite);
                      const sInfo = getStatusBadge(t.statut);
                      const isSelected = selectedTicket && selectedTicket.id === t.id;
                      return (
                        <div
                          key={t.id}
                          onClick={() => setSelectedTicket(t)}
                          style={{
                            padding: '16px', borderBottom: '1px solid #F3F4F6', cursor: 'pointer', transition: 'background 0.2s',
                            background: isSelected ? '#E1F5EE' : 'transparent'
                          }}
                          onMouseEnter={(e) => { if (!isSelected) e.currentTarget.style.background = '#FAF9F5'; }}
                          onMouseLeave={(e) => { if (!isSelected) e.currentTarget.style.background = 'transparent'; }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '6px' }}>
                            <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', background: pInfo.bg, color: pInfo.text, fontWeight: '500' }}>
                              {pInfo.icon} {t.priorite}
                            </span>
                            <span style={{ fontSize: '11px', padding: '2px 6px', borderRadius: '10px', background: sInfo.bg, color: sInfo.text, border: `1px solid ${sInfo.border}`, fontWeight: '600' }}>
                              {sInfo.label}
                            </span>
                          </div>
                          
                          <div style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937', marginBottom: '4px', textOverflow: 'ellipsis', overflow: 'hidden', whiteSpace: 'nowrap' }}>
                            {t.sujet}
                          </div>
                          
                          <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#73726C' }}>
                            <span>👤 {t.client_nom}</span>
                            <span>{t.updated_at ? new Date(t.updated_at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' }) : ''}</span>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Messagerie de Support (Right) */}
              <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F9FAFB' }}>
                {selectedTicket ? (
                  <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                    
                    {/* Chat Header */}
                    <div style={{ padding: '16px 24px', background: 'white', borderBottom: '1px solid #E8E7E3', display: 'flex', alignItems: 'center', justifyItems: 'space-between' }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <h4 style={{ margin: 0, fontSize: '15px', fontWeight: '600', color: '#1F2937' }}>{selectedTicket.sujet}</h4>
                          <span style={{ fontSize: '11px', padding: '3px 8px', background: '#FAF9F5', border: '1px solid #E8E7E3', borderRadius: '4px', color: '#73726C' }}>
                            📁 {selectedTicket.categorie}
                          </span>
                        </div>
                        <div style={{ fontSize: '12px', color: '#73726C', marginTop: '4px' }}>
                          Client : <strong>{selectedTicket.client_nom}</strong> | SIREN : {selectedTicket.client_id?.split('_')[1] || ''}
                        </div>
                      </div>
                      
                      {/* Ticket Status Select Dropdown */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label style={{ fontSize: '12px', color: '#4B5563', fontWeight: '500' }}>Statut :</label>
                        <select
                          value={selectedTicket.statut}
                          onChange={(e) => handleStatusChange(e.target.value)}
                          style={{ padding: '6px 12px', borderRadius: '8px', border: '1px solid #E8E7E3', fontSize: '13px', background: 'white', fontWeight: '500', outline: 'none' }}
                        >
                          <option value="ouvert">Ouvert</option>
                          <option value="en_cours">En cours</option>
                          <option value="resolu">Résolu</option>
                          <option value="ferme">Fermé</option>
                        </select>
                      </div>
                    </div>

                    {/* Chat Messages Panel */}
                    <div style={{ flex: 1, padding: '24px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '16px', background: '#FAF9F5' }}>
                      
                      {/* Ticket Description (First Message) */}
                      <div style={{ alignSelf: 'flex-start', maxWidth: '80%', padding: '12px 16px', borderRadius: '12px', background: 'white', border: '1px solid #E8E7E3', boxShadow: '0 1px 2px rgba(0,0,0,0.02)' }}>
                        <div style={{ fontSize: '11px', color: '#9CA3AF', fontWeight: '500', marginBottom: '6px' }}>
                          👤 {selectedTicket.client_nom} (Message initial)
                        </div>
                        <div style={{ fontSize: '13px', color: '#1F2937', whiteSpace: 'pre-wrap' }}>
                          {selectedTicket.description}
                        </div>
                        <div style={{ fontSize: '10px', color: '#9CA3AF', textAlign: 'right', marginTop: '6px' }}>
                          {selectedTicket.created_at ? new Date(selectedTicket.created_at).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : ''}
                        </div>
                      </div>

                      {/* Conversation History thread */}
                      {(selectedTicket.messages || []).map((msg, index) => {
                        // Ignorer le premier message s'il s'agit exactement de la description déjà affichée ci-dessus
                        if (index === 0 && msg.text === selectedTicket.description && msg.sender === 'client') return null;
                        
                        const isAdmin = msg.sender === 'admin';
                        return (
                          <div
                            key={index}
                            style={{
                              alignSelf: isAdmin ? 'flex-end' : 'flex-start',
                              maxWidth: '80%',
                              padding: '12px 16px',
                              borderRadius: '12px',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.02)',
                              background: isAdmin ? '#1D9E75' : 'white',
                              color: isAdmin ? 'white' : '#1F2937',
                              border: isAdmin ? 'none' : '1px solid #E8E7E3'
                            }}
                          >
                            <div style={{ fontSize: '10px', color: isAdmin ? '#E1F5EE' : '#9CA3AF', fontWeight: '500', marginBottom: '4px' }}>
                              {isAdmin ? '🛠️ Cabinet (Vous)' : `👤 ${selectedTicket.client_nom}`}
                            </div>
                            <div style={{ fontSize: '13px', whiteSpace: 'pre-wrap' }}>
                              {msg.text}
                            </div>
                            <div style={{ fontSize: '9px', color: isAdmin ? '#E1F5EE' : '#9CA3AF', textAlign: 'right', marginTop: '4px' }}>
                              {msg.date ? new Date(msg.date).toLocaleString('fr-FR', { hour: '2-digit', minute: '2-digit', day: 'numeric', month: 'short' }) : ''}
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Chat Input Footer */}
                    <form onSubmit={handleSendReply} style={{ padding: '16px 24px', background: 'white', borderTop: '1px solid #E8E7E3', display: 'flex', gap: '12px', alignItems: 'center' }}>
                      <textarea
                        value={replyText}
                        onChange={(e) => setReplyText(e.target.value)}
                        placeholder="Rédigez votre réponse au client..."
                        required
                        rows={2}
                        style={{ flex: 1, padding: '12px', border: '1px solid #E8E7E3', borderRadius: '8px', fontSize: '13px', outline: 'none', resize: 'none', fontFamily: 'inherit' }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSendReply(e);
                          }
                        }}
                      />
                      <button
                        type="submit"
                        disabled={actionLoading || !replyText.trim()}
                        style={{
                          padding: '12px 24px', background: actionLoading || !replyText.trim() ? '#E5E7EB' : '#1D9E75',
                          color: actionLoading || !replyText.trim() ? '#9CA3AF' : 'white',
                          border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', transition: 'all 0.2s', height: '48px'
                        }}
                      >
                        {actionLoading ? 'Envoi...' : 'Répondre'}
                      </button>
                    </form>

                  </div>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#73726C' }}>
                    <div style={{ fontSize: '48px', marginBottom: '16px' }}>✉️</div>
                    <h3 style={{ margin: 0 }}>Aucun ticket sélectionné</h3>
                    <p style={{ margin: '8px 0 0', fontSize: '13px' }}>Sélectionnez un ticket dans la boîte de réception pour afficher la conversation et répondre.</p>
                  </div>
                )}
              </div>

            </div>
          )}

          {/* TAB 5: ADMIN SETTINGS */}
          {activeTab === 'parametres' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
              
              {pwdMessage && (
                <div style={{
                  padding: '12px 16px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  background: pwdMessage.type === 'success' ? '#E1F5EE' : '#FCE8E6',
                  color: pwdMessage.type === 'success' ? '#0F6E56' : '#C53030',
                  border: pwdMessage.type === 'success' ? '1px solid #5DCAA5' : '1px solid #EF4444'
                }}>
                  {pwdMessage.text}
                </div>
              )}

              {/* CARD: PASSWORD MODIFICATION FOR ADMIN */}
              <div style={{ background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid #E8E7E3' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                  <span style={{ fontSize: '24px' }}>🔒</span>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', margin: 0, color: '#1F2937' }}>Mot de passe administrateur</h3>
                </div>
                
                <p style={{ fontSize: '14px', color: '#73726C', lineHeight: '1.5', marginBottom: '20px' }}>
                  Ce mot de passe n'est pas stocké en base : il est défini par le serveur.
                </p>

                <div style={{ padding: '16px', background: '#F5F4ED', borderRadius: '8px', fontSize: '13px', color: '#73726C', lineHeight: '1.6' }}>
                  Pour le modifier, il faut changer la variable
                  <code style={{ margin: '0 4px', padding: '2px 6px', background: '#EDECE6', borderRadius: '4px' }}>ADMIN_PASSWORD</code>
                  du serveur puis redémarrer l'API. Cette opération est réservée à l'exploitant.
                </div>
              </div>

            </div>
          )}

        </div>
      </div>

      {/* MODAL DETAILED CLIENT SHEET */}
      {selectedClient && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.5)', zIndex: 2000,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '20px'
        }} onClick={() => setSelectedClient(null)}>
          <div className="admin-modal-card" onClick={(e) => e.stopPropagation()}>
            
            {/* Modal Header */}
            <div style={{ background: '#1F2937', color: 'white', padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                <div style={{ width: '42px', height: '42px', borderRadius: '50%', background: '#1D9E75', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '18px' }}>
                  {selectedClient.nom.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{selectedClient.nom}</h3>
                  <span style={{ fontSize: '12px', color: '#D1D5DB' }}>Dossier Client Comptable</span>
                </div>
              </div>
              <button 
                onClick={() => setSelectedClient(null)}
                style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer', lineHeight: '1' }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body */}
            <div className="admin-modal-body" style={{ padding: '24px 28px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              <div className="admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#73726C', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Identifiant Client</label>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937', fontFamily: 'monospace', background: '#FAF9F5', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E8E7E3' }}>
                    {selectedClient.id}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#73726C', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Schéma & Utilisateur DB</label>
                  <div style={{ fontSize: '13px', fontWeight: '600', color: '#1F2937', fontFamily: 'monospace', background: '#FAF9F5', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E8E7E3' }}>
                    {selectedClient.db_user || `user_${selectedClient.id}`}
                  </div>
                </div>
              </div>

              <div className="admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#73726C', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>SIREN</label>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', background: '#FAF9F5', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E8E7E3' }}>
                    {selectedClient.siren || 'Non renseigné'}
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#73726C', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Adresse E-mail</label>
                  <div style={{ fontSize: '14px', fontWeight: '500', color: '#1F2937', background: '#FAF9F5', padding: '8px 12px', borderRadius: '6px', border: '1px solid #E8E7E3' }}>
                    {selectedClient.email || 'Pas d\'email'}
                  </div>
                </div>
              </div>

              <div className="admin-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#73726C', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Régime de TVA</label>
                  <div style={{ marginTop: '4px' }}>
                    <span style={{ fontSize: '12px', padding: '6px 12px', background: '#E8F4FD', color: '#1D4ED8', borderRadius: '12px', border: '1px solid #B8DFF8', fontWeight: '600' }}>
                      {getRegimeLabel(selectedClient.regime_tva)}
                    </span>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', fontSize: '11px', color: '#73726C', fontWeight: '600', textTransform: 'uppercase', marginBottom: '6px' }}>Création</label>
                  <div style={{ fontSize: '13px', fontWeight: '500', color: '#4B5563', marginTop: '6px' }}>
                    📅 {selectedClient.created_at ? new Date(selectedClient.created_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                  </div>
                </div>
              </div>

              <div className="admin-modal-footer">
                <span style={{
                  fontSize: '11px', padding: '4px 10px', borderRadius: '12px', fontWeight: '600',
                  background: selectedClient.actif ? '#E1F5EE' : '#FCEBEB',
                  color: selectedClient.actif ? '#0F6E56' : '#A32D2D',
                  border: selectedClient.actif ? '1px solid #5DCAA5' : '1px solid #F5B5B5'
                }}>
                  {selectedClient.actif ? '● ACTIF' : '● INACTIF'}
                </span>

                <div className="admin-modal-actions">
                  <button
                    onClick={() => handleDeleteClient(selectedClient.id, selectedClient.nom)}
                    style={{
                      background: '#EF4444', color: 'white', border: 'none', borderRadius: '8px',
                      padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      transition: 'background 0.2s', display: 'flex', alignItems: 'center', gap: '8px'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#DC2626'}
                    onMouseLeave={(e) => e.target.style.background = '#EF4444'}
                  >
                    🗑️ Supprimer le Client
                  </button>
                  <button
                    onClick={() => setSelectedClient(null)}
                    style={{
                      background: '#F3F4F6', color: '#4B5563', border: '1px solid #D1D5DB', borderRadius: '8px',
                      padding: '10px 16px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                      transition: 'background 0.2s'
                    }}
                    onMouseEnter={(e) => e.target.style.background = '#E5E7EB'}
                    onMouseLeave={(e) => e.target.style.background = '#F3F4F6'}
                  >
                    Fermer
                  </button>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminDashboard;
